import { useState } from 'react';
import { Dialog, DialogContent, DialogActions, Box, Tabs, Tab, Typography, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import { primarySoft } from '../../../../theme';
import ColoredDialogTitle from './creation/shared/ColoredDialogTitle';
import OfferCodeTab from './creation/barcode/OfferCodeTab';
import { BarcodeFormat, BarcodeResult, CARD_NUMBER_RESULT, buildOfferResult } from './creation/barcode/barcodeResult';

export type { BarcodeResult } from './creation/barcode/barcodeResult';

interface InsertBarcodeDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (result: BarcodeResult) => void;
  offerCount: number;
  offerTitles?: string[];
}

function InsertBarcodeDialog({ open, onClose, onConfirm, offerCount, offerTitles }: InsertBarcodeDialogProps) {
  const [tab, setTab] = useState(0);
  const [selectedOffer, setSelectedOffer] = useState(0);
  const [barcodeFormat, setBarcodeFormat] = useState<BarcodeFormat>('barcode_ean');

  const handleConfirm = () => {
    onConfirm(tab === 0 ? buildOfferResult(selectedOffer, barcodeFormat) : CARD_NUMBER_RESULT);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}>
      <ColoredDialogTitle onClose={onClose}>Code opération</ColoredDialogTitle>

      <Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
        <Tabs
          value={tab}
          onChange={(_, value: number) => setTab(value)}
          sx={{
            '& .MuiTabs-indicator': { backgroundColor: 'primary.main' },
            '& .MuiTab-root.Mui-selected': { color: 'primary.main' },
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, fontSize: '0.85rem' },
          }}
        >
          <Tab label="Codes opération" />
          <Tab label="Codes cartes" />
        </Tabs>
      </Box>

      <DialogContent sx={{ pt: 2.5, px: 3 }}>
        {tab === 0 ? (
          <OfferCodeTab
            offerCount={offerCount}
            offerTitles={offerTitles}
            selectedOffer={selectedOffer}
            onSelectOffer={setSelectedOffer}
            format={barcodeFormat}
            onFormatChange={setBarcodeFormat}
          />
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.85rem' }}>
              Insérer le numéro de carte du client
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1.25, borderRadius: 1.5, border: '1.5px solid', borderColor: 'primary.main', backgroundColor: theme => primarySoft(theme, 0.05) }}>
              <QrCode2Icon sx={{ fontSize: '1.1rem', color: 'primary.main' }} />
              <Box>
                <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 600, color: 'primary.main' }}>
                  Code carte
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', fontFamily: theme => theme.typography.fontFamilyMono }}>
                  {CARD_NUMBER_RESULT.token}
                </Typography>
              </Box>
              <CheckIcon sx={{ fontSize: '0.9rem', color: 'primary.main', ml: 'auto' }} />
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid', borderColor: 'grey.200', px: 3, py: 1.5, gap: 1 }}>
        <Button variant="outlined" startIcon={<CloseIcon fontSize="small" />} onClick={onClose}
          sx={{ textTransform: 'none', fontSize: '0.82rem', color: 'text.secondary', borderColor: 'divider', borderRadius: 1.5, '&:hover': { borderColor: 'grey.500', backgroundColor: 'grey.50' } }}>
          Annuler
        </Button>
        <Button variant="contained" startIcon={<CheckIcon fontSize="small" />} onClick={handleConfirm}
          sx={{ textTransform: 'none', fontSize: '0.82rem', fontWeight: 600, borderRadius: 1.5, backgroundColor: 'success.main', '&:hover': { backgroundColor: 'success.dark' } }}>
          Confirmer
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default InsertBarcodeDialog;
